// src/pages/AdminTop5Editor.jsx
import { useEffect, useRef, useState } from "react";
import api from "../services/api";

const MAX_MB = 8;

const emptySong = (rank) => ({
  rank,
  title: "",
  artist: "",
  overview: "",
  artistDetails: "",
  spotifyInput: "",
  imageUrl: "",
});

function parseSpotifyTrackId(input) {
  const m = String(input || "").match(/(track\/|spotify:track:)?([0-9A-Za-z]{22})/);
  return m ? m[2] : "";
}

export default function AdminTop5Editor() {
  const [rows, setRows] = useState([1, 2, 3, 4, 5].map(emptySong));
  const [files, setFiles] = useState({});       // { rank: File }
  const [previews, setPreviews] = useState({}); // { rank: url }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const blobUrlsRef = useRef({}); // { rank: objectUrl }

  const update = (rank, key, val) => {
    setRows((r) => r.map((s) => (s.rank === rank ? { ...s, [key]: val } : s)));
    if (key === "imageUrl") {
      setPreviews((p) => ({
        ...p,
        [rank]:
          val && /^https?:\/\//i.test(val)
            ? val
            : (p[rank] && files[rank] ? p[rank] : ""),
      }));
    }
  };

  const handleFile = (rank, file) => {
    setErr("");
    // revoke previous blob
    const prev = blobUrlsRef.current[rank];
    if (prev) {
      URL.revokeObjectURL(prev);
      delete blobUrlsRef.current[rank];
    }

    if (!file) {
      setFiles((f) => {
        const copy = { ...f };
        delete copy[rank];
        return copy;
      });
      const fromUrl = rows.find((x) => x.rank === rank)?.imageUrl || "";
      setPreviews((p) => ({ ...p, [rank]: fromUrl }));
      return;
    }

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_MB) {
      setErr(`Image #${rank} is too large. Max ${MAX_MB}MB.`);
      return;
    }

    const obj = URL.createObjectURL(file);
    blobUrlsRef.current[rank] = obj;
    setFiles((f) => ({ ...f, [rank]: file }));
    setPreviews((p) => ({ ...p, [rank]: obj }));
  };

  const clearFile = (rank) => handleFile(rank, null);

  useEffect(() => {
    return () => {
      Object.values(blobUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
      blobUrlsRef.current = {};
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");

    for (const s of rows) {
      if (!s.title.trim() || !s.artist.trim()) {
        setSaving(false);
        setErr(`Row #${s.rank}: title and artist are required.`);
        return;
      }
      if (!parseSpotifyTrackId(s.spotifyInput)) {
        setSaving(false);
        setErr(`Row #${s.rank}: Spotify URL or 22-char track ID is required.`);
        return;
      }
    }

    const songs = rows.map((r) => ({
      rank: r.rank,
      title: r.title.trim(),
      artist: r.artist.trim(),
      overview: r.overview,
      artistDetails: r.artistDetails,
      spotifyTrackId: parseSpotifyTrackId(r.spotifyInput),
      imageUrl: r.imageUrl || undefined,
    }));

    const fd = new FormData();
    fd.append("data", new Blob([JSON.stringify({ songs })], { type: "application/json" }));
    Object.entries(files).forEach(([rank, file]) => {
      if (file) fd.append(`image${rank}`, file);
    });

    try {
      await api.put("/api/admin/top5", fd);
      setMsg("Top 5 updated!");
    } catch (e) {
      const t = e?.response?.data;
      setErr(typeof t === "string" ? t : t?.message || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="page space-y-6">
      {/* Header */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Top 5 Editor</h1>
            <p className="mt-1 text-slate-300">
              Update this week’s Top 5. Upload artwork or provide an image URL. Spotify accepts a full URL or a 22-char track ID.
            </p>
          </div>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? "Saving…" : "Save Top 5"}
          </button>
        </div>

        {(msg || err) && (
          <div className="mt-3">
            {msg && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-emerald-200">
                {msg}
              </div>
            )}
            {err && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
                {err}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Rows */}
      <section className="grid gap-4 md:grid-cols-2">
        {rows.map((s) => {
          const previewSrc =
            previews[s.rank] ||
            (/^https?:\/\//i.test(s.imageUrl) ? s.imageUrl : "");

          // ✅ plain const (no hooks in loops)
          const spotifyId = parseSpotifyTrackId(s.spotifyInput);

          return (
            <article
              key={s.rank}
              className={`card ${s.rank === 1 ? "md:col-span-2" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <span className="badge bg-brand-600/25 text-slate-100">#{s.rank}</span>
                  <h2 className="text-xl font-semibold text-white">
                    {s.title || "Untitled"}{" "}
                    <span className="text-slate-400">— {s.artist || "Artist"}</span>
                  </h2>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-[160px_1fr]">
                {/* Artwork */}
                <div className="space-y-2">
                  {previewSrc ? (
                    <img
                      src={previewSrc}
                      alt={`Artwork #${s.rank}`}
                      className="h-40 w-40 rounded-xl object-cover border border-white/10"
                    />
                  ) : (
                    <div className="h-40 w-40 rounded-xl border border-white/10 bg-white/5 grid place-items-center text-[11px] text-slate-400">
                      No image
                    </div>
                  )}

                  <div className="flex flex-col gap-2">
                    <label className="text-[11px] uppercase tracking-wider text-slate-400">
                      Upload artwork
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFile(s.rank, e.target.files?.[0])}
                      className="input file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-slate-100 hover:file:bg-white/15"
                    />
                    {files[s.rank] && (
                      <button type="button" className="btn-secondary" onClick={() => clearFile(s.rank)}>
                        Remove upload
                      </button>
                    )}
                  </div>
                </div>

                {/* Fields */}
                <div className="grid gap-3">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <LabeledInput
                      label="Title *"
                      value={s.title}
                      onChange={(v) => update(s.rank, "title", v)}
                      placeholder="Track title"
                    />
                    <LabeledInput
                      label="Artist *"
                      value={s.artist}
                      onChange={(v) => update(s.rank, "artist", v)}
                      placeholder="Artist name"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <LabeledInput
                      label="Image URL (optional)"
                      value={s.imageUrl}
                      onChange={(v) => update(s.rank, "imageUrl", v)}
                      placeholder="https://…"
                    />
                    <LabeledInput
                      label="Spotify URL or ID *"
                      value={s.spotifyInput}
                      onChange={(v) => update(s.rank, "spotifyInput", v)}
                      placeholder="https://open.spotify.com/track/… or 22-char ID"
                      mono
                    />
                  </div>

                  <LabeledTextarea
                    label="Track overview"
                    value={s.overview}
                    onChange={(v) => update(s.rank, "overview", v)}
                    placeholder="2–3 sentences about the track"
                  />
                  <LabeledTextarea
                    label="Artist details"
                    value={s.artistDetails}
                    onChange={(v) => update(s.rank, "artistDetails", v)}
                    placeholder="Short artist context/bio"
                  />

                  {/* Optional Spotify preview */}
                  {spotifyId && (
                    <div className="overflow-hidden rounded-xl">
                      <iframe
                        title={`spotify-${s.rank}`}
                        src={`https://open.spotify.com/embed/track/${spotifyId}`}
                        width="100%"
                        height="152"
                        className="block"
                        style={{ border: 0, backgroundColor: "transparent" }}
                        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </section>

      {/* Save bar */}
      <section className="card">
        <div className="flex items-center justify-between">
          <div className="text-slate-400 text-sm">
            Make sure each row has a title, artist, and a valid Spotify track.
          </div>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? "Saving…" : "Save Top 5"}
          </button>
        </div>
      </section>
    </form>
  );
}

/* ------- Small form bits ------- */

function LabeledInput({ label, value, onChange, placeholder, mono = false }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </label>
      <input
        className={`input w-full ${mono ? "font-mono" : ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function LabeledTextarea({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-400 mb-1">
        {label}
      </label>
      <textarea
        className="input w-full min-h-[84px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}
